import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { ProductManagementPageModel } from "../pageModels/ProductManagementPageModel";
import { Product } from "../../models/Product";
import { Modal, Input, Select, Button, Table, message, Descriptions, Space } from "antd";

const model = new ProductManagementPageModel();

const ProductManagement: React.FC = observer(() => {
    const [forceOfflineModalVisible, setForceOfflineModalVisible] = useState(false);
    const [offlineReason, setOfflineReason] = useState("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [offlineRecordsVisible, setOfflineRecordsVisible] = useState(false);

    useEffect(() => {
        model.loadProducts();
    }, []);

    const handleSearch = () => {
        model.loadProducts();
    };

    const handleReset = () => {
        model.resetFilters();
        model.loadProducts();
    };

    const handleForceOffline = async () => {
        if (!offlineReason.trim()) {
            message.error("请输入下架理由");
            return;
        }

        const success = await model.forceOffline(selectedProductId, offlineReason);
        if (success) {
            message.success("操作成功");
            setForceOfflineModalVisible(false);
            setOfflineReason("");
            setSelectedProductId("");
        }
    };

    const showForceOfflineModal = (productId: string) => {
        setSelectedProductId(productId);
        setForceOfflineModalVisible(true);
    };

    const columns = [
        {
            title: "商品ID",
            dataIndex: "id",
            key: "id",
            width: 120
        },
        {
            title: "商品名称",
            dataIndex: "name",
            key: "name",
            width: 200
        },
        {
            title: "卖家昵称",
            dataIndex: "sellerNickname",
            key: "sellerNickname",
            width: 120
        },
        {
            title: "商品状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status: number) => status === 1 ? "已上架" : "未上架"
        },
        {
            title: "最近曝光数",
            dataIndex: "recentExposures",
            key: "recentExposures",
            width: 100
        },
        {
            title: "最近点击数",
            dataIndex: "recentClicks",
            key: "recentClicks",
            width: 100
        },
        {
            title: "订单数",
            dataIndex: "orderCount",
            key: "orderCount",
            width: 100
        },
        {
            title: "完成数",
            dataIndex: "completedCount",
            key: "completedCount",
            width: 100
        }
    ];

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "20px", display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                <Input
                    placeholder="商品ID/名称"
                    value={model.searchText}
                    onChange={e => model.setSearchText(e.target.value)}
                    style={{ width: 200 }}
                />
                <Select
                    placeholder="商品状态"
                    value={model.selectedStatus}
                    onChange={model.setSelectedStatus}
                    style={{ width: 120 }}
                    allowClear
                >
                    <Select.Option value={1}>已上架</Select.Option>
                    <Select.Option value={0}>未上架</Select.Option>
                </Select>
                <Select
                    placeholder="排序方式"
                    value={model.sortField + "_" + model.sortOrder}
                    onChange={(value) => {
                        const [field, order] = value.split("_");
                        model.setSortField(field);
                        model.setSortOrder(order as "asc" | "desc");
                        model.loadProducts();
                    }}
                    style={{ width: 160 }}
                >
                    <Select.Option value="onlineTime_desc">上架时间倒序</Select.Option>
                    <Select.Option value="recentExposures_asc">曝光数正序</Select.Option>
                    <Select.Option value="recentExposures_desc">曝光数倒序</Select.Option>
                    <Select.Option value="orderCount_asc">订单数正序</Select.Option>
                    <Select.Option value="orderCount_desc">订单数倒序</Select.Option>
                    <Select.Option value="completedCount_asc">完成数正序</Select.Option>
                    <Select.Option value="completedCount_desc">完成数倒序</Select.Option>
                </Select>
                <Button type="primary" onClick={handleSearch}>
                    查询
                </Button>
                <Button onClick={handleReset}>重置</Button>
            </div>

            <Table
                columns={columns}
                dataSource={model.products}
                rowKey="id"
                onRow={(record) => ({
                    onClick: () => model.showProductDetail(record)
                })}
                style={{ backgroundColor: "white", cursor: "pointer" }}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <div>{model.selectedProduct?.name}</div>
                        <Space style={{ marginLeft: 20 }}>
                            {model.selectedProduct?.status === 1 && (
                                <Button 
                                    type="primary" 
                                    danger 
                                    onClick={() => showForceOfflineModal(model.selectedProduct!.id)}
                                >
                                    强制下架
                                </Button>
                            )}
                            <Button type="link" style={{ padding: 0 }} onClick={() => setOfflineRecordsVisible(true)}>
                                下架记录
                            </Button>
                        </Space>
                    </div>
                }
                open={model.showDetailModal}
                onCancel={model.closeDetailModal}
                footer={null}
                width={800}
            >
                {model.selectedProduct && (
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px' }}>基本信息</div>
                        <Descriptions bordered column={4} size="small" labelStyle={{ padding: '4px 6px', fontSize: 12 }} contentStyle={{ padding: '4px 6px', fontSize: 12 }}>
                            <Descriptions.Item label="商品ID">{model.selectedProduct.id}</Descriptions.Item>
                            <Descriptions.Item label="卖家ID">{model.selectedProduct.sellerId}</Descriptions.Item>
                            <Descriptions.Item label="卖家昵称">{model.selectedProduct.sellerNickname}</Descriptions.Item>
                            <Descriptions.Item label="商品状态">{model.selectedProduct.status === 1 ? "已上架" : "未上架"}</Descriptions.Item>
                            <Descriptions.Item label="一级分类">{model.selectedProduct.categoryLevel1}</Descriptions.Item>
                            <Descriptions.Item label="二级分类">{model.selectedProduct.categoryLevel2}</Descriptions.Item>
                            <Descriptions.Item label="草稿状态">
                                {model.selectedProduct.draftStatus === 0 ? "无新版本" :
                                 model.selectedProduct.draftStatus === 1 ? "新版本编辑中" : "新版本审核中"}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">{model.selectedProduct.createTime}</Descriptions.Item>
                            <Descriptions.Item label="上架时间">{model.selectedProduct.onlineTime || "未上架"}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px', marginTop: '10px' }}>数据统计</div>
                        <Descriptions bordered column={4} size="small" layout="vertical" labelStyle={{ padding: '0 6px', fontSize: 12 }} contentStyle={{ padding: '2px 6px', fontSize: 12, lineHeight: 1.1 }}>
                            <Descriptions.Item label="最近曝光数">{model.selectedProduct.recentExposures}</Descriptions.Item>
                            <Descriptions.Item label="最近点击数">{model.selectedProduct.recentClicks}</Descriptions.Item>
                            <Descriptions.Item label="订单数">{model.selectedProduct.orderCount}</Descriptions.Item>
                            <Descriptions.Item label="完成数">{model.selectedProduct.completedCount}</Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>

            <Modal
                title="强制下架商品"
                open={forceOfflineModalVisible}
                onOk={handleForceOffline}
                onCancel={() => {
                    setForceOfflineModalVisible(false);
                    setOfflineReason("");
                    setSelectedProductId("");
                }}
            >
                <div style={{ marginBottom: "16px" }}>确认要强制下架该商品吗？</div>
                <Input.TextArea
                    placeholder="请输入下架理由"
                    value={offlineReason}
                    onChange={e => setOfflineReason(e.target.value)}
                    rows={4}
                />
            </Modal>

            <Modal
                title="下架记录"
                open={offlineRecordsVisible}
                onCancel={() => setOfflineRecordsVisible(false)}
                footer={null}
                width={720}
            >
                {model.selectedProduct ? (
                    model.selectedProduct.forceOfflineRecords.length === 0 ? (
                        <div style={{ color: '#999' }}>暂无记录</div>
                    ) : (
                        <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 6 }}>
                            {model.selectedProduct.forceOfflineRecords.map((record, index) => (
                                <div key={index} style={{ padding: '4px 6px', background: index % 2 === 0 ? '#fafafa' : '#fff', border: '1px solid #f0f0f0', borderRadius: 0, marginBottom: 6, fontSize: 12 }}>
                                    <Space size={12}>
                                        <span>下架时间：{record.offlineTime}</span>
                                        <span>下架理由：{record.reason}</span>
                                        <span>操作员：{record.operator}</span>
                                    </Space>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div style={{ color: '#999' }}>暂无数据</div>
                )}
            </Modal>
        </div>
    );
});

export default ProductManagement; 